import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import appLogo from '../../assets/branding/logo.png';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../config/constants';

function AndroidIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-9 w-9" aria-hidden="true">
      <path fill="currentColor" d="M7.2 6.2 5.7 3.6a.7.7 0 0 1 1.2-.7l1.6 2.8A9.8 9.8 0 0 1 12 5c1.3 0 2.5.2 3.5.7l1.6-2.8a.7.7 0 1 1 1.2.7l-1.5 2.6A6.9 6.9 0 0 1 20 11H4a6.9 6.9 0 0 1 3.2-4.8ZM8 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM4 12h16v6.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18.5V12Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-9 w-9" aria-hidden="true">
      <path fill="currentColor" d="M16.7 12.7c0-2.5 2.1-3.7 2.2-3.8a4.8 4.8 0 0 0-3.8-2.1c-1.6-.2-3.1 1-3.9 1-.8 0-2-1-3.3-1-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.3 2.5 1.3-.1 1.8-.8 3.4-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.2-2.5a11 11 0 0 0 1.4-2.9 4.4 4.4 0 0 1-3-3.9Zm-2.6-7.6A4.4 4.4 0 0 0 15.2 2a4.5 4.5 0 0 0-2.9 1.5 4.1 4.1 0 0 0-1.1 3c1.1.1 2.2-.5 2.9-1.4Z" />
    </svg>
  );
}

function PlayTriangleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0" aria-hidden="true">
      <path fill="currentColor" d="M4 3.6c0-.9.98-1.45 1.73-.97l14.2 8.9c.7.44.7 1.5 0 1.94l-14.2 8.9C4.98 22.85 4 22.3 4 21.4V3.6Z" />
    </svg>
  );
}

function GooglePlayBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get it on Google Play"
      className="flex w-full items-center justify-center gap-3 rounded-xl bg-black px-5 py-2.5 text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-neutral-900"
    >
      <PlayTriangleIcon />
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-medium uppercase tracking-wide text-white/80">Get it on</span>
        <span className="block text-xl font-semibold">Google Play</span>
      </span>
    </a>
  );
}

function AppStoreBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download on the App Store"
      className="flex w-full items-center justify-center gap-3 rounded-xl bg-black px-5 py-2.5 text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-neutral-900"
    >
      <AppleIcon />
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-medium text-white/80">Download on the</span>
        <span className="block text-xl font-semibold">App Store</span>
      </span>
    </a>
  );
}

function StoreCard({ icon, title, subtitle, children, accent }) {
  return (
    <section className={`rounded-3xl border bg-white p-6 shadow-sm ${accent}`}>
      <div className="flex items-start gap-4">
        <div className="rounded-2xl bg-slate-900 p-3 text-white">{icon}</div>
        <div>
          <h2 className="text-xl font-black text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-relaxed text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export default function DownloadAppsPage() {
  const { t } = useTranslation();
  const iosAvailable = Boolean(APP_STORE_URL);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-indigo-50 via-white to-violet-50 px-4 py-12">
      <div className="mx-auto max-w-5xl">
        <div className="mx-auto max-w-2xl text-center">
          <img src={appLogo} alt="SublingoX" className="mx-auto h-20 w-20 rounded-3xl shadow-xl shadow-indigo-300/40" />
          <p className="mt-6 text-xs font-black uppercase tracking-[0.25em] text-indigo-600">{t('home.badge')}</p>
          <h1 className="mt-2 text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
            {t('nav.downloadApp')}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            {t('home.subtitle')}
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <StoreCard
            icon={<AndroidIcon />}
            title={t('download.android')}
            accent="border-emerald-200"
          >
            <GooglePlayBadge href={PLAY_STORE_URL} />
          </StoreCard>

          <StoreCard
            icon={<AppleIcon />}
            title={t('download.iosDevices')}
            subtitle={iosAvailable ? undefined : t('langModal.comingSoon')}
            accent="border-slate-200"
          >
            {iosAvailable ? (
              <AppStoreBadge href={APP_STORE_URL} />
            ) : (
              <div className="flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 px-5 py-3.5 text-sm font-black text-slate-500">
                {t('langModal.comingSoon')}
              </div>
            )}
          </StoreCard>
        </div>

        <div className="mt-8 rounded-3xl border border-indigo-100 bg-white/80 p-6 text-center shadow-sm backdrop-blur">
          <Link to="/" className="mt-4 inline-flex rounded-xl border border-indigo-200 px-5 py-2.5 text-sm font-bold text-indigo-700 transition hover:bg-indigo-50">
            {t('common.back')}
          </Link>
        </div>
      </div>
    </div>
  );
}
